import { Injectable, inject } from '@angular/core';
import { Observable, from, switchMap } from 'rxjs';
import { ApiService } from './api.service';
import { WorkspaceDefinition } from '@crowdctrl/core';

export type StructureType = 'queues' | 'forms' | 'boards';

export interface StructureListResponse<T> {
  items: T[];
  pagination: {
    limit: number;
    hasNext: boolean;
    nextCursor: string | null;
    totalCount?: number;
    retrievedAt: string;
  };
}

type EmptyBody = Record<string, never>;

/**
 * Service for workspace definition management API calls.
 */
@Injectable({
  providedIn: 'root',
})
export class WorkspaceDefinitionService {
  private apiService = inject(ApiService);
  private baseUrl: string = '';

  /**
   * Set the base URL for API calls (should be called from app initialization)
   */
  setBaseUrl(url: string): void {
    this.baseUrl = url;
  }

  /**
   * Get all workspace definitions for a tenant with optional pagination.
   */
  getDefinitions(
    tenantId: string,
    limit?: number,
    cursor?: string,
    options?: {
      includeDeleted?: boolean;
      status?: string;
      latestOnly?: boolean;
      definitionId?: string;
    },
  ): Observable<{
    definitions: WorkspaceDefinition[];
    pagination: {
      limit: number;
      hasNext: boolean;
      nextCursor: string | null;
      totalCount: number;
      retrievedAt: string;
    };
  }> {
    const params: Record<string, string | number | boolean> = {};
    if (limit) {
      params['limit'] = limit;
    }
    if (cursor) {
      params['cursor'] = cursor;
    }
    if (options?.includeDeleted !== undefined) {
      params['includeDeleted'] = options.includeDeleted;
    }
    if (options?.status) {
      params['status'] = options.status;
    }
    if (options?.latestOnly !== undefined) {
      params['latestOnly'] = options.latestOnly;
    }
    if (options?.definitionId) {
      params['definitionId'] = options.definitionId;
    }
    return from(
      this.apiService.get<{
        definitions: WorkspaceDefinition[];
        pagination: {
          limit: number;
          hasNext: boolean;
          nextCursor: string | null;
          totalCount: number;
          retrievedAt: string;
        };
      }>(
        this.baseUrl,
        `/workspace_definitions_api/api/tenants/${tenantId}/workspaceDefinitions`,
        Object.keys(params).length > 0 ? params : undefined,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Get a single workspace definition by ID
   */
  getDefinition(
    tenantId: string,
    definitionId: string,
  ): Observable<{ definition: WorkspaceDefinition }> {
    return from(
      this.apiService.get<{ definition: WorkspaceDefinition }>(
        this.baseUrl,
        `/workspace_definitions_api/api/tenants/${tenantId}/workspaceDefinitions/${definitionId}`,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Create a new workspace definition
   */
  createDefinition(
    tenantId: string,
    definition: Partial<WorkspaceDefinition>,
  ): Observable<{ definition: WorkspaceDefinition }> {
    return from(
      this.apiService.post<{ definition: WorkspaceDefinition }, Partial<WorkspaceDefinition>>(
        this.baseUrl,
        `/workspace_definitions_api/api/tenants/${tenantId}/workspaceDefinitions`,
        definition,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Update an existing workspace definition
   */
  updateDefinition(
    tenantId: string,
    definitionId: string,
    definition: Partial<WorkspaceDefinition>,
  ): Observable<{ definition: WorkspaceDefinition }> {
    return from(
      this.apiService.put<{ definition: WorkspaceDefinition }, Partial<WorkspaceDefinition>>(
        this.baseUrl,
        `/workspace_definitions_api/api/tenants/${tenantId}/workspaceDefinitions/${definitionId}`,
        definition,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Update thumbnail URL for a workspace definition
   */
  updateThumbnail(
    tenantId: string,
    definitionId: string,
    thumbnailUrl: string | null,
  ): Observable<{ definition: WorkspaceDefinition }> {
    return this.updateDefinition(tenantId, definitionId, {
      thumbnailUrl: thumbnailUrl || undefined,
    });
  }

  /**
   * Soft delete a workspace definition
   */
  deleteDefinition(tenantId: string, definitionId: string): Observable<{ success: boolean }> {
    return from(
      this.apiService.delete<{ success: boolean }>(
        this.baseUrl,
        `/workspace_definitions_api/api/tenants/${tenantId}/workspaceDefinitions/${definitionId}`,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Publish a draft workspace definition
   */
  publishDefinition(
    tenantId: string,
    definitionId: string,
  ): Observable<{ definition: WorkspaceDefinition }> {
    return from(
      this.apiService.post<{ definition: WorkspaceDefinition }, EmptyBody>(
        this.baseUrl,
        `/workspace_definitions_api/api/tenants/${tenantId}/workspaceDefinitions/${definitionId}/publish`,
        {},
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Toggle availability for a published or superseded definition
   */
  updateAvailability(
    tenantId: string,
    definitionId: string,
    isAvailable: boolean,
  ): Observable<{ definition: WorkspaceDefinition }> {
    return from(
      this.apiService.put<{ definition: WorkspaceDefinition }, { isAvailable: boolean }>(
        this.baseUrl,
        `/workspace_definitions_api/api/tenants/${tenantId}/workspaceDefinitions/${definitionId}/availability`,
        { isAvailable },
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Create a new draft from a published workspace definition
   */
  createDraftFromPublished(
    tenantId: string,
    definitionId: string,
  ): Observable<{ definition: WorkspaceDefinition }> {
    return from(
      this.apiService.post<{ definition: WorkspaceDefinition }, EmptyBody>(
        this.baseUrl,
        `/workspace_definitions_api/api/tenants/${tenantId}/workspaceDefinitions/${definitionId}/draft`,
        {},
      ),
    ).pipe(switchMap((obs) => obs));
  }

  listStructureDefinitions<T>(
    tenantId: string,
    definitionId: string,
    type: StructureType,
    limit?: number,
    cursor?: string,
  ): Observable<StructureListResponse<T>> {
    const params: Record<string, string | number | boolean> = {};
    if (limit) {
      params['limit'] = limit;
    }
    if (cursor) {
      params['cursor'] = cursor;
    }
    return from(
      this.apiService.get<StructureListResponse<T>>(
        this.baseUrl,
        `/workspace_definitions_api/api/tenants/${tenantId}/workspaceDefinitions/${definitionId}/structures/${type}`,
        Object.keys(params).length > 0 ? params : undefined,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  getStructureDefinition<T>(
    tenantId: string,
    definitionId: string,
    type: StructureType,
    itemId: string,
  ): Observable<{ item: T }> {
    return from(
      this.apiService.get<{ item: T }>(
        this.baseUrl,
        `/workspace_definitions_api/api/tenants/${tenantId}/workspaceDefinitions/${definitionId}/structures/${type}/${itemId}`,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  createStructureDefinition<T extends object>(
    tenantId: string,
    definitionId: string,
    type: StructureType,
    payload: T,
  ): Observable<{ item: T }> {
    return from(
      this.apiService.post<{ item: T }, T>(
        this.baseUrl,
        `/workspace_definitions_api/api/tenants/${tenantId}/workspaceDefinitions/${definitionId}/structures/${type}`,
        payload,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  updateStructureDefinition<T extends object>(
    tenantId: string,
    definitionId: string,
    type: StructureType,
    itemId: string,
    payload: Partial<T>,
  ): Observable<{ item: T }> {
    return from(
      this.apiService.put<{ item: T }, Partial<T>>(
        this.baseUrl,
        `/workspace_definitions_api/api/tenants/${tenantId}/workspaceDefinitions/${definitionId}/structures/${type}/${itemId}`,
        payload,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  deleteStructureDefinition(
    tenantId: string,
    definitionId: string,
    type: StructureType,
    itemId: string,
  ): Observable<{ success: boolean }> {
    return from(
      this.apiService.delete<{ success: boolean }>(
        this.baseUrl,
        `/workspace_definitions_api/api/tenants/${tenantId}/workspaceDefinitions/${definitionId}/structures/${type}/${itemId}`,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  migrateDefinitionStructures(
    tenantId: string,
    definitionId: string,
  ): Observable<{ success: boolean; migrated: Record<string, number> }> {
    return from(
      this.apiService.post<
        {
          success: boolean;
          migrated: Record<string, number>;
        },
        EmptyBody
      >(
        this.baseUrl,
        `/workspace_definitions_api/api/tenants/${tenantId}/workspaceDefinitions/${definitionId}/structures/migrate`,
        {},
      ),
    ).pipe(switchMap((obs) => obs));
  }
}
